import { z } from 'zod';
import { USER_ROLES, emailSchema, objectIdSchema } from '@tailoring-crm/shared';
import { isApiRequestError } from '../api/errors.js';

/**
 * What `/api/v1/auth/*` answers, as schemas the browser validates against.
 *
 * These live here rather than in the shared package because the auth endpoints predate it in
 * this codebase's history and their request shapes are defined server-side; what the browser
 * needs is the RESPONSE shape, and validating it here means a contract drift shows up as a
 * loud parse failure on the login screen rather than as `undefined` rendered into a heading.
 *
 * The primitives that both sides must agree on — what a valid email address is, what a PIN is —
 * do come from the shared package, so the form rejects exactly what the endpoint would.
 */

export const roleSchema = z.enum(USER_ROLES);

export const authUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  phone: z.string().nullable(),
});
export type AuthUser = z.infer<typeof authUserSchema>;

export const authShopSchema = z.object({
  id: z.string(),
  name: z.string(),
  plan: z.string(),
  subscriptionStatus: z.string(),
  trialEndsAt: z.string(),
  // Nullish rather than required: a session issued before this field existed is still a valid
  // session, and refusing to parse it would sign the shop out on deploy day.
  currentPeriodEndsAt: z.string().nullish().default(null),
});
export type AuthShop = z.infer<typeof authShopSchema>;

/** One row of the shop picker. */
export const shopChoiceSchema = z.object({
  tenantId: z.string(),
  name: z.string(),
  role: roleSchema,
});
export type ShopChoice = z.infer<typeof shopChoiceSchema>;

/**
 * The one success body every credential path answers with — login, signup, shop selection and
 * refresh alike. `accessToken` is deliberately ignored by this app: the browser is
 * authenticated by httpOnly cookies, and a token kept in JavaScript is a token an injected
 * script can read.
 */
export const authenticatedSchema = z.object({
  status: z.literal('authenticated'),
  user: authUserSchema,
  shop: authShopSchema.nullable(),
  role: roleSchema,
  sessionId: z.string(),
  expiresIn: z.number(),
});
export type Authenticated = z.infer<typeof authenticatedSchema>;

/** The other thing a login can answer: "which of your shops?" */
export const selectShopChallengeSchema = z.object({
  status: z.literal('select_shop'),
  selectionToken: z.string(),
  expiresIn: z.number(),
  shops: z.array(shopChoiceSchema).min(1),
});
export type SelectShopChallenge = z.infer<typeof selectShopChallengeSchema>;

export const loginResultSchema = z.discriminatedUnion('status', [
  authenticatedSchema,
  selectShopChallengeSchema,
]);
export type LoginResult = z.infer<typeof loginResultSchema>;

/** What `/signup/start` and `/password-reset/start` answer: an OTP is on its way. */
export const otpChallengeSchema = z.object({
  /** `98•••••210` — proof we sent it to the right number, useless to anybody reading over a shoulder. */
  sentTo: z.string(),
  expiresAt: z.string(),
  /** Nothing may be resent before this moment; the button counts down to it. */
  resendAfter: z.string(),
  codeLength: z.number(),
  /** `console` on a developer machine, where the code is printed to the API log. */
  provider: z.string().optional(),
});
export type OtpChallenge = z.infer<typeof otpChallengeSchema>;

/** Proof that an address answered an emailed code, good for one form, once. */
export const verificationSchema = z.object({
  verificationToken: z.string(),
  expiresIn: z.number(),
});
export type Verification = z.infer<typeof verificationSchema>;

export const meSchema = z.object({
  user: authUserSchema,
  shop: authShopSchema.nullable(),
  role: roleSchema,
  sessionId: z.string(),
  memberships: z.array(shopChoiceSchema),
});
export type Me = z.infer<typeof meSchema>;

export const sessionSummarySchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  role: roleSchema,
  createdAt: z.string(),
  lastUsedAt: z.string(),
  expiresAt: z.string(),
  userAgent: z.string().nullable(),
  ip: z.string().nullable(),
  /** The device the person is reading this list on. It is never offered a plain "sign out". */
  current: z.boolean(),
});
export type SessionSummary = z.infer<typeof sessionSummarySchema>;

export const sessionListSchema = z.object({ items: z.array(sessionSummarySchema) });

export const signedOutSchema = z.object({ status: z.string() });

export const passwordResetDoneSchema = z.object({
  status: z.literal('password_reset'),
  revokedSessions: z.number(),
});

export const pinSetSchema = z.object({ status: z.literal('pin_set'), userId: z.string() });

// --- Reading a refusal ---------------------------------------------------------------------

/**
 * Seconds until a locked-out caller may try again.
 *
 * The API answers 429 with `retryAfterSeconds` in `details` (and in a `Retry-After` header the
 * browser does not expose to `fetch` across origins). The number matters: "too many attempts"
 * with no end in sight reads as a permanent ban to someone who has simply fat-fingered a PIN
 * three times, and they ring the shop owner instead of waiting thirty seconds.
 */
export function retryAfterSeconds(error: unknown): number | null {
  if (!isApiRequestError(error)) return null;
  const value = error.details?.retryAfterSeconds;
  return typeof value === 'number' && Number.isFinite(value) ? Math.ceil(value) : null;
}

export function isRateLimited(error: unknown): boolean {
  return isApiRequestError(error) && error.status === 429;
}

/** Input shapes, so a caller cannot transpose two strings and only find out at runtime. */
export const emailInputSchema = z.object({ email: emailSchema });
export const tenantChoiceSchema = z.object({ tenantId: objectIdSchema });
